import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../api';
import { FRECUENCIA_GLOBAL_OPTIONS, labelFrecuencia } from './informeIvaFrequencyOptions';

const DIAS = [
  { v: 0, label: 'Domingo' },
  { v: 1, label: 'Lunes' },
  { v: 2, label: 'Martes' },
  { v: 3, label: 'Miércoles' },
  { v: 4, label: 'Jueves' },
  { v: 5, label: 'Viernes' },
  { v: 6, label: 'Sábado' }
];

function Section({ title, children }) {
  return (
    <section className="border-b border-slate-200 last:border-0 py-6 first:pt-0">
      <h2 className="text-base font-semibold text-slate-900 mb-4">{title}</h2>
      {children}
    </section>
  );
}

export default function InformeIvaAdministracionPage() {
  const [cfg, setCfg] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const patch = (partial) => {
    setCfg((c) => ({ ...c, ...partial }));
    setDirty(true);
  };

  const loadCfg = useCallback(async () => {
    const { data } = await api.get('/informes-iva/config/global');
    setCfg(data);
    setDirty(false);
  }, []);

  const loadResumen = useCallback(async () => {
    const { data } = await api.get('/informes-iva/administracion/resumen');
    setResumen(data);
  }, []);

  useEffect(() => {
    Promise.all([loadCfg(), loadResumen()]).catch((e) => toast.error(e.response?.data?.error || e.message));
  }, [loadCfg, loadResumen]);

  const saveCfg = async () => {
    if (!cfg) return;
    if (cfg.autoSendGlobalEnabled && !window.confirm('¿Activar envío automático al cliente (AUTO_SEND)?')) return;
    if (cfg.modoOperacion === 'PRODUCCION' && !window.confirm('¿Activar PRODUCCIÓN? Se podrán enviar correos reales a clientes.')) return;

    const periodoInicio = Math.min(28, Math.max(1, Number(cfg.periodoIvaDiaInicio) || 4));
    const periodoCierre = Math.min(28, Math.max(1, Number(cfg.periodoIvaDiaCierre) || 18));

    setBusy(true);
    try {
      await api.put('/informes-iva/config/global', {
        modoOperacion: cfg.modoOperacion || 'PRUEBA',
        frecuenciaGlobalInformes: cfg.frecuenciaGlobalInformes || 'SEMANAL',
        digestMode: cfg.digestMode,
        digestPorContadorEnabled: cfg.digestPorContadorEnabled,
        digestDestinoPruebaEmail: cfg.digestDestinoPruebaEmail,
        digestScheduleEnabled: cfg.digestScheduleEnabled,
        digestCronDayOfWeek: cfg.digestCronDayOfWeek,
        digestCronHour: cfg.digestCronHour,
        digestCronMinute: cfg.digestCronMinute,
        scheduledCronHour: cfg.scheduledCronHour,
        scheduledCronMinute: cfg.scheduledCronMinute,
        scheduledMaxEjecucionesMes: cfg.scheduledMaxEjecucionesMes,
        periodoIvaDiaInicio: periodoInicio,
        periodoIvaDiaCierre: periodoCierre,
        persistInformeFiles: cfg.persistInformeFiles,
        emailOficina: cfg.emailOficina,
        frescuraHoras: cfg.frescuraHoras,
        timezone: cfg.timezone,
        retencionDias: cfg.retencionDias,
        autoSyncOnRun: cfg.autoSyncOnRun,
        syncF29OnRun: cfg.syncF29OnRun,
        scheduledRunsEnabled: cfg.scheduledRunsEnabled,
        autoSendGlobalEnabled: cfg.autoSendGlobalEnabled,
        permitirRemanenteCeroAsumido: cfg.permitirRemanenteCeroAsumido
      });
      toast.success('Guardado');
      await loadCfg();
      await loadResumen();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) {
    return <div className="max-w-4xl mx-auto p-10 text-slate-500">Cargando…</div>;
  }

  const modoPrueba = (cfg.modoOperacion || 'PRUEBA') !== 'PRODUCCION';
  const ventana = resumen?.ventanaPeriodo;
  const diaIni = cfg.periodoIvaDiaInicio ?? 4;
  const diaCie = cfg.periodoIvaDiaCierre ?? 18;
  const preFin = diaCie > 1 ? diaCie - 1 : 28;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 pb-20">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <Link to="/informes-iva" className="text-sm text-indigo-600 hover:underline">
            ← Panel del mes
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 mt-2">Administración Informes IVA</h1>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={saveCfg}
          className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {dirty ? 'Guardar cambios' : 'Guardar'}
        </button>
      </div>

      {ventana?.mensaje && (
        <div className="mb-6 rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-3 text-sm text-indigo-950">
          {ventana.mensaje}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-6">
        <Section title="1. Ambiente">
          <div className="flex flex-wrap gap-3 items-center">
            <select
              className="border rounded-lg px-3 py-2 text-sm min-w-[180px]"
              value={cfg.modoOperacion || 'PRUEBA'}
              onChange={(e) => patch({ modoOperacion: e.target.value })}
            >
              <option value="PRUEBA">Piloto (sin mail al cliente)</option>
              <option value="PRODUCCION">Producción</option>
            </select>
            <span className="text-sm text-slate-600">
              {modoPrueba ? 'Revisa informes en el panel antes de enviar.' : 'Permite envío al cliente según aprobación.'}
            </span>
          </div>
        </Section>

        <Section title="2. Informes automáticos">
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={Boolean(cfg.scheduledRunsEnabled)} onChange={(e) => patch({ scheduledRunsEnabled: e.target.checked })} />
              Activar (sync SII + cálculo según frecuencia; no es un informe diario a todas las empresas)
            </label>

            <div className="grid sm:grid-cols-3 gap-4">
              <label className="text-sm block">
                <span className="text-slate-700">Frecuencia global</span>
                <select className="mt-1 w-full border rounded-lg px-3 py-2" value={cfg.frecuenciaGlobalInformes || 'SEMANAL'} onChange={(e) => patch({ frecuenciaGlobalInformes: e.target.value })}>
                  {FRECUENCIA_GLOBAL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm block">
                <span className="text-slate-700">Hora del control diario</span>
                <div className="mt-1 flex gap-2">
                  <input type="number" min={0} max={23} className="w-full border rounded-lg px-2 py-2" value={cfg.scheduledCronHour ?? 6} onChange={(e) => patch({ scheduledCronHour: Number(e.target.value) })} />
                  <input type="number" min={0} max={59} className="w-full border rounded-lg px-2 py-2" value={cfg.scheduledCronMinute ?? 0} onChange={(e) => patch({ scheduledCronMinute: Number(e.target.value) })} />
                </div>
              </label>
              <label className="text-sm block">
                <span className="text-slate-700">Tope / empresa / mes</span>
                <input type="number" min={1} max={31} className="mt-1 w-full border rounded-lg px-3 py-2" value={cfg.scheduledMaxEjecucionesMes ?? 8} onChange={(e) => patch({ scheduledMaxEjecucionesMes: Number(e.target.value) })} />
              </label>
            </div>

            <p className="text-xs text-slate-500">
              Por empresa: Panel → Cliente → frecuencia. Global = {labelFrecuencia(cfg.frecuenciaGlobalInformes || 'SEMANAL')}.
            </p>

            <button
              type="button"
              disabled={busy}
              className="text-sm text-indigo-600 hover:underline"
              onClick={async () => {
                if (!window.confirm('¿Encolar ahora las empresas que tocan?')) return;
                setBusy(true);
                try {
                  const { data } = await api.post('/informes-iva/jobs/scheduled-run');
                  if (data.skipped) toast.message(`Sin cola: ${data.skipped}`);
                  else toast.success(`${data.enqueued ?? 0} en cola`);
                } catch (e) {
                  toast.error(e.response?.data?.error || e.message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Probar cola automática ahora
            </button>
          </div>
        </Section>

        <Section title="3. Calendario IVA (Chile)">
          <p className="text-sm text-slate-600 mb-4 leading-relaxed">
            Ejemplo para el período <strong>enero</strong>: los informes <strong>pre-IVA</strong> arrancan el día que indique en{' '}
            <strong>enero</strong>. El <strong>IVA estimado de cierre</strong> de ese mismo enero arranca el día que indique en{' '}
            <strong>febrero</strong> (mes siguiente).
          </p>

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm mb-4 font-mono leading-relaxed text-slate-800">
            <div>Enero (mes tributario)</div>
            <div className="pl-2 mt-1">
              día {diaIni} ene ─── pre-IVA ─── hasta {preFin} feb
            </div>
            <div className="pl-2 mt-1">
              día {diaCie} feb ─── IVA estimado final de enero ─── (hasta inicia pre-IVA de febrero)
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 max-w-xl">
            <label className="text-sm block">
              <span className="font-medium text-slate-800">Inicio pre-IVA</span>
              <span className="block text-xs text-slate-500 mt-0.5">Día del mes tributario (ej. 4 de enero)</span>
              <input type="number" min={1} max={28} className="mt-2 w-full border rounded-lg px-3 py-2" value={diaIni} onChange={(e) => patch({ periodoIvaDiaInicio: Number(e.target.value) })} />
            </label>
            <label className="text-sm block">
              <span className="font-medium text-slate-800">Inicio cierre estimado</span>
              <span className="block text-xs text-slate-500 mt-0.5">Día del mes siguiente (ej. 18 de febrero para enero)</span>
              <input type="number" min={1} max={28} className="mt-2 w-full border rounded-lg px-3 py-2" value={diaCie} onChange={(e) => patch({ periodoIvaDiaCierre: Number(e.target.value) })} />
            </label>
          </div>
          {resumen?.calendarioIva?.resumen && <p className="text-xs text-slate-500 mt-3">{resumen.calendarioIva.resumen}</p>}
        </Section>

        <Section title="4. Correo resumen semanal (opcional)">
          <p className="text-sm text-slate-600 mb-3">Lista de empresas a revisar. No reemplaza el informe PDF al cliente.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input type="checkbox" checked={cfg.digestScheduleEnabled !== false} onChange={(e) => patch({ digestScheduleEnabled: e.target.checked })} />
              Programar envío automático
            </label>
            <select className="border rounded-lg px-3 py-2 text-sm" value={Number(cfg.digestCronDayOfWeek ?? 1)} onChange={(e) => patch({ digestCronDayOfWeek: Number(e.target.value) })}>
              {DIAS.map((d) => (
                <option key={d.v} value={d.v}>
                  {d.label}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <input type="number" min={0} max={23} placeholder="Hora" className="w-full border rounded-lg px-2 py-2 text-sm" value={cfg.digestCronHour ?? 8} onChange={(e) => patch({ digestCronHour: Number(e.target.value) })} />
              <input type="number" min={0} max={59} placeholder="Min" className="w-full border rounded-lg px-2 py-2 text-sm" value={cfg.digestCronMinute ?? 0} onChange={(e) => patch({ digestCronMinute: Number(e.target.value) })} />
            </div>
            {modoPrueba ? (
              <input type="email" className="sm:col-span-2 border rounded-lg px-3 py-2 text-sm" placeholder="Correo prueba (vacío = super admin)" value={cfg.digestDestinoPruebaEmail || ''} onChange={(e) => patch({ digestDestinoPruebaEmail: e.target.value })} />
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input type="checkbox" checked={Boolean(cfg.digestPorContadorEnabled)} onChange={(e) => patch({ digestPorContadorEnabled: e.target.checked })} />
                  Enviar a contadores / oficina
                </label>
                {cfg.digestPorContadorEnabled && (
                  <>
                    <select className="border rounded-lg px-3 py-2 text-sm" value={cfg.digestMode || 'POR_ASIGNADO'} onChange={(e) => patch({ digestMode: e.target.value })}>
                      <option value="POR_ASIGNADO">Por contador IVA</option>
                      <option value="GLOBAL_OFICINA">Oficina</option>
                      <option value="AMBOS">Ambos</option>
                    </select>
                    <input type="email" className="border rounded-lg px-3 py-2 text-sm" placeholder="Email oficina" value={cfg.emailOficina || ''} onChange={(e) => patch({ emailOficina: e.target.value })} />
                  </>
                )}
              </>
            )}
          </div>
        </Section>

        <Section title="5. Avanzado">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={Boolean(cfg.autoSyncOnRun)} onChange={(e) => patch({ autoSyncOnRun: e.target.checked })} />
              Sync SII al calcular manual
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={cfg.syncF29OnRun !== false} onChange={(e) => patch({ syncF29OnRun: e.target.checked })} />
              Incluir F29 en sync
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={Boolean(cfg.persistInformeFiles)} onChange={(e) => patch({ persistInformeFiles: e.target.checked })} />
              Guardar PDF en servidor
            </label>
            <label className="flex items-center gap-2 text-red-800">
              <input type="checkbox" checked={Boolean(cfg.autoSendGlobalEnabled)} onChange={(e) => patch({ autoSendGlobalEnabled: e.target.checked })} />
              AUTO_SEND al cliente
            </label>
          </div>
          <Link to="/informes-iva/correo" className="inline-block mt-4 text-sm text-indigo-600 hover:underline">
            Plantillas y SMTP → Correo y envíos
          </Link>
        </Section>
      </div>
    </div>
  );
}
